import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const PAGE_SIZE_OPTIONS = [20, 50, 100];

/** Page numbers to show: first, last, current ±1, with 'gap' where pages are skipped. */
function pageWindow(page: number, totalPages: number): (number | 'gap')[] {
  const keep = new Set([1, totalPages, page - 1, page, page + 1]);
  const out: (number | 'gap')[] = [];
  for (let p = 1; p <= totalPages; p++) {
    if (keep.has(p)) out.push(p);
    else if (out[out.length - 1] !== 'gap') out.push('gap');
  }
  return out;
}

/** List footer: "Page X of Y · N total", Rows 20/50/100, First/Prev, page numbers, Next/Last. */
export function ListPagination({
  page, totalPages, total, pageSize, onPageChange, onPageSizeChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/40 pt-4 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-muted-foreground">
          Page {page} of {totalPages} · {total} total
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Rows</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-9 w-20 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {totalPages > 1 && (
        // size="icon" for the same reason as PaginationBar: size="sm" carries a sm:h-9
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" aria-label="First page" disabled={page <= 1} onClick={() => onPageChange(1)}>
            <ChevronsLeft className="h-4 w-4 shrink-0" />
          </Button>
          <Button variant="outline" size="icon" aria-label="Previous page" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            <ChevronLeft className="h-4 w-4 shrink-0" />
          </Button>
          <div className="hidden items-center gap-1.5 sm:flex">
            {pageWindow(page, totalPages).map((p, i) =>
              p === 'gap' ? (
                <span key={`gap-${i}`} className="px-1 text-muted-foreground">…</span>
              ) : (
                <Button
                  key={p}
                  variant={p === page ? 'default' : 'outline'}
                  size="icon"
                  aria-label={`Page ${p}`}
                  aria-current={p === page ? 'page' : undefined}
                  onClick={() => p !== page && onPageChange(p)}
                >
                  {p}
                </Button>
              ),
            )}
          </div>
          <Button variant="outline" size="icon" aria-label="Next page" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            <ChevronRight className="h-4 w-4 shrink-0" />
          </Button>
          <Button variant="outline" size="icon" aria-label="Last page" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)}>
            <ChevronsRight className="h-4 w-4 shrink-0" />
          </Button>
        </div>
      )}
    </div>
  );
}
